import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fileUrl, downloadFile, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Mic, FileText, Upload, Sparkles, Download, Calendar, Building2,
  History, FileAudio, Wand2, ListChecks, CheckCircle2, Clock, User, Trash2, Users, Quote, Play, Pencil, FileSpreadsheet,
} from "lucide-react";
import { toast } from "sonner";

const SectionTitle = ({ icon: Icon, children, right }) => (
  <div className="flex items-center justify-between mb-4">
    <div className="flex items-center gap-2">
      <Icon size={16} className="text-primary" strokeWidth={2} />
      <h3 className="font-heading font-bold text-sm tracking-[0.15em] uppercase">{children}</h3>
    </div>
    {right}
  </div>
);

export default function MeetingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [meeting, setMeeting] = useState(null);
  const [depts, setDepts] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [eTitle, setETitle] = useState("");
  const [eDate, setEDate] = useState("");
  const [eDept, setEDept] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [analysisSource, setAnalysisSource] = useState("transcript");
  const [selectedRecording, setSelectedRecording] = useState("");
  const [transcribing, setTranscribing] = useState(false);
  const [summarizing, setSummarizing] = useState(false);
  const [summarizingSpeakers, setSummarizingSpeakers] = useState(false);
  const [uploadingRec, setUploadingRec] = useState(false);
  const [uploadingMin, setUploadingMin] = useState(false);
  const recInput = useRef();
  const minInput = useRef();

  const load = async () => {
    try {
      const { data } = await api.get(`/meetings/${id}`);
      setMeeting(data);
      if (data.recordings?.length && !selectedRecording) {
        setSelectedRecording(data.recordings[0].id);
      }
    } catch {
      toast.error("Meeting not found");
      navigate("/");
    }
  };

  useEffect(() => {
    load();
    if (isAdmin) api.get("/departments").then((r) => setDepts(r.data.departments));
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    if (!meeting) return;
    const hasT = !!meeting.transcript_current;
    const hasM = !!meeting.minutes_current;
    setAnalysisSource((prev) => {
      if (prev === "transcript" && hasT) return prev;
      if (prev === "minutes" && hasM) return prev;
      return hasT ? "transcript" : (hasM ? "minutes" : "transcript");
    });
  }, [meeting]);

  const openEdit = () => {
    setETitle(meeting.title);
    setEDate(meeting.date);
    setEDept(meeting.department);
    setEditOpen(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!eTitle.trim() || !eDate || !eDept) {
      toast.error("Lengkapi judul, tanggal, dan departemen");
      return;
    }
    setSavingEdit(true);
    try {
      const { data } = await api.put(`/meetings/${id}`, { title: eTitle.trim(), date: eDate, department: eDept });
      setMeeting(data);
      setEditOpen(false);
      toast.success("Rapat diperbarui");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingEdit(false);
    }
  };

  const uploadRecording = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingRec(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/meetings/${id}/recordings`, fd);
      setMeeting(data);
      if (!selectedRecording && data.recordings.length) setSelectedRecording(data.recordings[data.recordings.length - 1].id);
      toast.success("Recording uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploadingRec(false);
      if (recInput.current) recInput.current.value = "";
    }
  };

  const uploadMinutes = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingMin(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/meetings/${id}/minutes`, fd);
      setMeeting(data);
      toast.success(meeting?.minutes_current ? "New version saved" : "Minutes uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploadingMin(false);
      if (minInput.current) minInput.current.value = "";
    }
  };

  const runTranscribe = async () => {
    if (!selectedRecording) {
      toast.error("Select a recording first");
      return;
    }
    setTranscribing(true);
    try {
      const { data } = await api.post(`/meetings/${id}/transcribe`, { recording_id: selectedRecording });
      setMeeting(data);
      toast.success("Transcript generated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setTranscribing(false);
    }
  };

  const runSummarize = async () => {
    setSummarizing(true);
    try {
      const { data } = await api.post(`/meetings/${id}/summarize`, { source: analysisSource });
      setMeeting(data);
      toast.success("AI summary generated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSummarizing(false);
    }
  };

  const runSpeakerSummary = async () => {
    setSummarizingSpeakers(true);
    try {
      const { data } = await api.post(`/meetings/${id}/speaker-summary`, { source: analysisSource });
      setMeeting(data);
      toast.success("Ringkasan per pembicara dibuat");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSummarizingSpeakers(false);
    }
  };

  const deleteMeeting = async () => {
    if (!window.confirm("Delete this meeting? This cannot be undone.")) return;
    await api.delete(`/meetings/${id}`);
    toast.success("Meeting deleted");
    navigate("/");
  };

  const doDownload = async (path, name) => {
    try {
      await downloadFile(path, name);
    } catch {
      toast.error("Gagal mengunduh file");
    }
  };

  const downloadSummaryPdf = async () => {
    try {
      const res = await api.get(`/meetings/${id}/summary-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ringkasan-${(meeting.title || "rapat").replace(/[^a-z0-9]+/gi, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF diunduh");
    } catch {
      toast.error("Gagal mengunduh PDF");
    }
  };

  const downloadActionItemsXlsx = async () => {
    try {
      const res = await api.get(`/meetings/${id}/action-items-xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `action-items-${(meeting.title || "rapat").replace(/[^a-z0-9]+/gi, "_")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel diunduh");
    } catch {
      toast.error("Gagal mengunduh Excel");
    }
  };

  if (!meeting)
    return (
      <Layout>
        <div className="p-12 text-muted-foreground" data-testid="detail-loading">Loading…</div>
      </Layout>
    );

  const t = meeting.transcript_current;
  const s = meeting.summary;
  const sp = meeting.speaker_summary;
  const hasTranscript = !!t;
  const hasMinutes = !!meeting.minutes_current;
  const sourceHasContent = analysisSource === "minutes" ? hasMinutes : hasTranscript;

  return (
    <Layout>
      <div className="p-8 lg:p-12 max-w-[1400px]">
        <button
          onClick={() => navigate("/")}
          data-testid="detail-back-btn"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors duration-150 mb-6"
        >
          <ArrowLeft size={16} /> Back to History
        </button>

        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap border-b border-border pb-6 mb-8">
          <div>
            <h1 className="font-heading font-black text-4xl lg:text-5xl tracking-tight" data-testid="meeting-title">
              {meeting.title}
            </h1>
            <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1.5 mono"><Calendar size={14} /> {meeting.date}</span>
              <span className="inline-flex items-center gap-1.5"><Building2 size={14} /> {meeting.department}</span>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {isAdmin && (
              <Button
                variant="ghost"
                onClick={openEdit}
                data-testid="edit-meeting-btn"
                className="rounded-none text-muted-foreground hover:text-foreground"
              >
                <Pencil size={16} className="mr-2" /> Edit
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={deleteMeeting}
              data-testid="delete-meeting-btn"
              className="rounded-none text-muted-foreground hover:text-primary"
            >
              <Trash2 size={16} className="mr-2" /> Delete
            </Button>
          </div>
        </div>

        {/* Edit meeting dialog */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="rounded-none" data-testid="edit-meeting-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading tracking-tight">Edit Rapat</DialogTitle>
            </DialogHeader>
            <form onSubmit={saveEdit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="etitle">Judul</Label>
                <Input
                  id="etitle"
                  data-testid="edit-title-input"
                  value={eTitle}
                  onChange={(ev) => setETitle(ev.target.value)}
                  className="rounded-none focus-visible:ring-2 focus-visible:ring-primary"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="edate">Tanggal</Label>
                <Input
                  id="edate"
                  type="date"
                  data-testid="edit-date-input"
                  value={eDate}
                  onChange={(ev) => setEDate(ev.target.value)}
                  className="rounded-none mono focus-visible:ring-2 focus-visible:ring-primary"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label>Departemen</Label>
                <Select value={eDept} onValueChange={setEDept}>
                  <SelectTrigger data-testid="edit-department-select" className="rounded-none">
                    <SelectValue placeholder="Pilih departemen" />
                  </SelectTrigger>
                  <SelectContent>
                    {[...new Set([eDept, ...depts].filter(Boolean))].map((d) => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button
                  type="submit"
                  disabled={savingEdit}
                  data-testid="save-meeting-btn"
                  className="rounded-none bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  {savingEdit ? "Menyimpan…" : "Simpan Perubahan"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Upload row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          {/* Recordings */}
          <div className="border border-border bg-card p-6">
            <SectionTitle icon={Mic}>Recordings</SectionTitle>
            {meeting.recordings?.length > 0 ? (
              <div className="space-y-2 mb-4">
                {meeting.recordings.map((r) => (
                  <RecordingPlayer key={r.id} rec={r} />
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground mb-4">No recordings uploaded yet.</p>
            )}
            <input ref={recInput} type="file" accept=".mp3,.wav,.m4a,.aac,audio/*" onChange={uploadRecording} className="hidden" data-testid="recording-file-input" />
            <Button
              variant="outline"
              onClick={() => recInput.current?.click()}
              disabled={uploadingRec}
              data-testid="upload-recording-btn"
              className="rounded-none w-full border-foreground/20"
            >
              <Upload size={16} className="mr-2" /> {uploadingRec ? "Uploading…" : "Upload Recording (MP3/WAV/AAC)"}
            </Button>
          </div>

          {/* Minutes */}
          <div className="border border-border bg-card p-6">
            <SectionTitle icon={FileText} right={
              meeting.minutes_versions?.length > 0 && (
                <VersionDialog
                  title="Minutes Version History"
                  items={meeting.minutes_versions}
                  render={(v) => (
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className="rounded-none mono">v{v.version}</Badge>
                      <span className="text-sm flex-1 truncate">{v.original_filename}</span>
                      <span className="mono text-xs text-muted-foreground">{v.uploaded_at?.slice(0, 10)}</span>
                      <button onClick={() => doDownload(v.storage_path, v.original_filename)} className="text-muted-foreground hover:text-primary" data-testid={`download-minutes-version-${v.version}`} title="Unduh"><Download size={14} /></button>
                    </div>
                  )}
                />
              )
            }>Manual Minutes</SectionTitle>
            {meeting.minutes_current ? (
              <div className="flex items-center gap-3 border border-border px-3 py-2 mb-4" data-testid="current-minutes">
                <FileText size={16} className="text-muted-foreground shrink-0" />
                <span className="text-sm truncate flex-1">{meeting.minutes_current.original_filename}</span>
                <Badge variant="outline" className="rounded-none mono text-[10px]">v{(meeting.minutes_versions?.length || 0) + 1}</Badge>
                <button onClick={() => doDownload(meeting.minutes_current.storage_path, meeting.minutes_current.original_filename)} className="text-muted-foreground hover:text-primary" data-testid="download-minutes" title="Unduh">
                  <Download size={15} />
                </button>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground mb-4">No minutes uploaded yet.</p>
            )}
            <input ref={minInput} type="file" accept=".pdf,.docx,.doc" onChange={uploadMinutes} className="hidden" data-testid="minutes-file-input" />
            <Button
              variant="outline"
              onClick={() => minInput.current?.click()}
              disabled={uploadingMin}
              data-testid="upload-minutes-btn"
              className="rounded-none w-full border-foreground/20"
            >
              <Upload size={16} className="mr-2" /> {uploadingMin ? "Uploading…" : meeting.minutes_current ? "Upload New Version" : "Upload Minutes (PDF/DOCX)"}
            </Button>
          </div>
        </div>

        {/* Main 2-column */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Transcript (left, 2/3) */}
          <div className="lg:col-span-2 border border-border bg-card p-6">
            <SectionTitle icon={FileAudio} right={
              t && meeting.transcript_versions?.length > 0 && (
                <VersionDialog
                  title="Transcript Version History"
                  items={meeting.transcript_versions}
                  render={(v) => (
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <Badge variant="outline" className="rounded-none mono">v{v.version}</Badge>
                        <span className="mono text-xs text-muted-foreground">{v.created_at?.slice(0, 16).replace("T", " ")}</span>
                      </div>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap max-h-40 overflow-auto">{v.text}</p>
                    </div>
                  )}
                />
              )
            }>Transcript</SectionTitle>

            <div className="flex flex-col sm:flex-row gap-3 mb-5 pb-5 border-b border-border">
              <Select value={selectedRecording} onValueChange={setSelectedRecording}>
                <SelectTrigger data-testid="transcribe-recording-select" className="rounded-none flex-1">
                  <SelectValue placeholder="Select a recording to transcribe" />
                </SelectTrigger>
                <SelectContent>
                  {meeting.recordings?.map((r) => (
                    <SelectItem key={r.id} value={r.id}>{r.original_filename}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={runTranscribe}
                disabled={transcribing || !meeting.recordings?.length}
                data-testid="generate-transcript-btn"
                className="rounded-none bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                <Wand2 size={16} className="mr-2" /> {transcribing ? "Transcribing…" : "Generate Transcript"}
              </Button>
            </div>

            {t ? (
              <div data-testid="transcript-content">
                <div className="mono text-xs text-muted-foreground mb-4">
                  Source: {t.source_filename} · {t.created_at?.slice(0, 16).replace("T", " ")}
                </div>
                <div className="space-y-4 max-w-2xl">
                  {t.text.split(/\n\n+/).map((para, i) => (
                    <p key={i} className="text-[15px] leading-7 text-foreground/90">{para}</p>
                  ))}
                </div>
              </div>
            ) : (
              <div className="py-16 text-center" data-testid="transcript-empty">
                <FileAudio size={32} className="mx-auto text-muted-foreground/40 mb-3" />
                <p className="text-muted-foreground text-sm">
                  {meeting.recordings?.length
                    ? "Select a recording and generate a transcript."
                    : "Upload a recording to enable transcription."}
                </p>
              </div>
            )}
          </div>

          {/* AI Summary (right, 1/3, sticky) */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-8 border border-border bg-card p-6">
              <SectionTitle icon={Sparkles}>AI Analysis</SectionTitle>

              {(hasTranscript || hasMinutes) && (
                <div className="mb-3">
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5 block">Sumber Analisis</label>
                  <Select value={analysisSource} onValueChange={setAnalysisSource}>
                    <SelectTrigger data-testid="analysis-source-select" className="rounded-none w-full text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="transcript" disabled={!hasTranscript}>
                        Transkrip{!hasTranscript ? " (belum ada)" : ""}
                      </SelectItem>
                      <SelectItem value="minutes" disabled={!hasMinutes}>
                        Notulen Manual{!hasMinutes ? " (belum ada)" : ""}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="mb-5 pb-5 border-b border-border" />

              <Button
                onClick={runSummarize}
                disabled={summarizing || !sourceHasContent}
                data-testid="generate-summary-btn"
                className="rounded-none w-full bg-primary hover:bg-primary/90 text-primary-foreground mb-3"
              >
                <Sparkles size={16} className="mr-2" /> {summarizing ? "Analyzing…" : "Generate AI Summary"}
              </Button>

              <Button
                variant="outline"
                onClick={runSpeakerSummary}
                disabled={summarizingSpeakers || !sourceHasContent}
                data-testid="generate-speaker-summary-btn"
                className="rounded-none w-full border-foreground/20 mb-5"
              >
                <Users size={16} className="mr-2" /> {summarizingSpeakers ? "Menganalisis…" : "Ringkasan per Pembicara"}
              </Button>

              {!hasTranscript && !hasMinutes && (
                <p className="text-xs text-muted-foreground" data-testid="summary-need-source">
                  Upload notulen manual atau buat transkrip untuk mengaktifkan analisis AI.
                </p>
              )}
              {(hasTranscript || hasMinutes) && !sourceHasContent && (
                <p className="text-xs text-muted-foreground" data-testid="summary-source-empty">
                  Sumber yang dipilih belum tersedia.
                </p>
              )}

              {s && (
                <div className="space-y-6" data-testid="summary-content">
                  <div className="mono text-[10px] uppercase tracking-wider text-muted-foreground">
                    {s.model_label} · {s.generated_at?.slice(0, 10)}{s.source ? ` · Sumber: ${s.source}` : ""}
                  </div>

                  <Button
                    variant="outline"
                    onClick={downloadSummaryPdf}
                    data-testid="download-summary-pdf-btn"
                    className="rounded-none w-full border-foreground/20 text-xs h-8"
                  >
                    <Download size={13} className="mr-1.5" /> Unduh PDF
                  </Button>

                  <div>
                    <h4 className="font-heading font-bold text-xs tracking-[0.15em] uppercase mb-2 text-muted-foreground">Overview</h4>
                    <p className="text-sm leading-relaxed" data-testid="summary-overview">{s.overview}</p>
                  </div>

                  <div>
                    <h4 className="font-heading font-bold text-xs tracking-[0.15em] uppercase mb-2 text-muted-foreground flex items-center gap-1.5">
                      <ListChecks size={13} /> Key Decisions
                    </h4>
                    <ul className="space-y-2" data-testid="key-decisions">
                      {(s.key_decisions || []).map((d, i) => (
                        <li key={i} className="text-sm flex gap-2">
                          <span className="text-primary font-bold mono">{String(i + 1).padStart(2, "0")}</span>
                          <span>{d}</span>
                        </li>
                      ))}
                      {(!s.key_decisions || s.key_decisions.length === 0) && (
                        <li className="text-sm text-muted-foreground">None recorded.</li>
                      )}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action Items table (full width) */}
        {s && (
          <div className="border border-border bg-card p-6 mt-6" data-testid="action-items-section">
            <SectionTitle icon={CheckCircle2} right={
              s.action_items?.length ? (
                <button
                  onClick={downloadActionItemsXlsx}
                  data-testid="download-action-items-xlsx-btn"
                  className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors duration-150"
                  title="Unduh Excel"
                >
                  <FileSpreadsheet size={14} /> Unduh Excel
                </button>
              ) : null
            }>Action Items</SectionTitle>
            {s.action_items?.length ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-foreground/80 text-left">
                      <th className="py-3 pr-4 font-heading font-bold text-xs tracking-[0.1em] uppercase text-muted-foreground">Task</th>
                      <th className="py-3 pr-4 font-heading font-bold text-xs tracking-[0.1em] uppercase text-muted-foreground w-40"><span className="inline-flex items-center gap-1"><User size={12} /> PIC</span></th>
                      <th className="py-3 font-heading font-bold text-xs tracking-[0.1em] uppercase text-muted-foreground w-40"><span className="inline-flex items-center gap-1"><Clock size={12} /> Deadline</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.action_items.map((a, i) => (
                      <tr key={i} className="border-b border-border" data-testid={`action-item-${i}`}>
                        <td className="py-3 pr-4">{a.task}</td>
                        <td className="py-3 pr-4">{a.pic}</td>
                        <td className="py-3">
                          <span className={`mono text-xs px-2 py-1 border ${a.deadline && a.deadline !== "TBD" ? "border-primary/50 text-primary" : "border-border text-muted-foreground"}`}>
                            {a.deadline || "TBD"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No action items identified.</p>
            )}
          </div>
        )}

        {/* Per-speaker summary (full width) */}
        {sp && (
          <div className="border border-border bg-card p-6 mt-6" data-testid="speaker-summary-section">
            <SectionTitle icon={Users} right={
              <span className="mono text-[10px] uppercase tracking-wider text-muted-foreground">
                {sp.model_label} · {sp.generated_at?.slice(0, 10)}
              </span>
            }>Ringkasan per Pembicara</SectionTitle>
            {sp.speakers?.length ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {sp.speakers.map((p, i) => (
                  <div key={i} className="border border-border p-4" data-testid={`speaker-${i}`}>
                    <div className="flex items-center gap-3 mb-3 pb-3 border-b border-border">
                      <div className="h-9 w-9 bg-primary/10 text-primary flex items-center justify-center shrink-0">
                        <User size={18} strokeWidth={2} />
                      </div>
                      <div className="min-w-0">
                        <div className="font-heading font-bold text-base tracking-tight truncate">{p.name}</div>
                        {p.role && p.role !== "-" && (
                          <div className="text-xs text-muted-foreground truncate">{p.role}</div>
                        )}
                      </div>
                    </div>
                    <ul className="space-y-2">
                      {(p.views || []).map((v, j) => (
                        <li key={j} className="text-sm flex gap-2 leading-relaxed">
                          <Quote size={13} className="text-primary shrink-0 mt-1" strokeWidth={2} />
                          <span>{v}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Tidak ada pembicara yang teridentifikasi menyampaikan pandangan.</p>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}

const RecordingPlayer = ({ rec }) => {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    return () => { if (src) URL.revokeObjectURL(src); };
  }, [src]);

  const loadAudio = async () => {
    if (src || loading) return;
    setLoading(true);
    try {
      const res = await api.get(`/files/${rec.storage_path}`, { responseType: "blob" });
      setSrc(URL.createObjectURL(res.data));
    } catch {
      toast.error("Gagal memuat audio");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border border-border px-3 py-2" data-testid={`recording-${rec.id}`}>
      <div className="flex items-center gap-3">
        <FileAudio size={16} className="text-muted-foreground shrink-0" />
        <span className="text-sm truncate flex-1">{rec.original_filename}</span>
        {!src && (
          <button
            onClick={loadAudio}
            disabled={loading}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary/80 transition-colors duration-150"
            data-testid={`play-recording-${rec.id}`}
            title="Putar"
          >
            <Play size={15} /> {loading ? "Memuat…" : "Putar"}
          </button>
        )}
      </div>
      {src && (
        <audio
          src={src}
          controls
          autoPlay
          controlsList="nodownload noplaybackrate"
          onContextMenu={(e) => e.preventDefault()}
          className="w-full mt-3 h-9"
          data-testid={`audio-recording-${rec.id}`}
        />
      )}
    </div>
  );
};

const VersionDialog = ({ title, items, render }) => (
  <Dialog>
    <DialogTrigger asChild>
      <button className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors duration-150" data-testid="version-history-btn">
        <History size={14} /> History ({items.length})
      </button>
    </DialogTrigger>
    <DialogContent className="rounded-none max-w-lg">
      <DialogHeader>
        <DialogTitle className="font-heading tracking-tight">{title}</DialogTitle>
      </DialogHeader>
      <div className="space-y-3 max-h-[60vh] overflow-auto">
        {[...items].reverse().map((v, i) => (
          <div key={i} className="border border-border p-3">{render(v)}</div>
        ))}
      </div>
    </DialogContent>
  </Dialog>
);
